import { z } from "zod";

// Contact form validation schema
export const contactFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, { message: "Name is required" })
    .max(100, { message: "Name must be less than 100 characters" }),
  email: z
    .string()
    .trim()
    .email({ message: "Invalid email address" })
    .max(255, { message: "Email must be less than 255 characters" }),
  subject: z
    .string()
    .trim()
    .min(1, { message: "Subject is required" })
    .max(200, { message: "Subject must be less than 200 characters" }),
  message: z
    .string()
    .trim()
    .min(1, { message: "Message is required" })
    .max(1000, { message: "Message must be less than 1000 characters" }),
});

// Beat upload validation schema
export const beatUploadSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, { message: "Title is required" })
    .max(200, { message: "Title must be less than 200 characters" }),
  producer: z
    .string()
    .trim()
    .min(1, { message: "Producer name is required" })
    .max(100, { message: "Producer name must be less than 100 characters" }),
  genre: z
    .string()
    .min(1, { message: "Genre is required" })
    .max(50, { message: "Genre must be less than 50 characters" }),
  bpm: z
    .number({ invalid_type_error: "BPM must be a number" })
    .int({ message: "BPM must be an integer" })
    .min(20, { message: "BPM must be at least 20" })
    .max(300, { message: "BPM must be less than 300" }),
  mp3_lease_price: z
    .number({ invalid_type_error: "Price must be a number" })
    .min(0, { message: "Price cannot be negative" })
    .max(10000, { message: "Price must be less than $10,000" }),
  wav_lease_price: z
    .number({ invalid_type_error: "Price must be a number" })
    .min(0, { message: "Price cannot be negative" })
    .max(10000, { message: "Price must be less than $10,000" }),
  flp_price: z
    .number({ invalid_type_error: "Price must be a number" })
    .min(0, { message: "Price cannot be negative" })
    .max(10000, { message: "Price must be less than $10,000" }),
  semi_exclusive_price: z
    .number({ invalid_type_error: "Price must be a number" })
    .min(0, { message: "Price cannot be negative" })
    .max(100000, { message: "Price must be less than $100,000" }),
  full_exclusive_price: z
    .number({ invalid_type_error: "Price must be a number" })
    .min(0, { message: "Price cannot be negative" })
    .max(100000, { message: "Price must be less than $100,000" }),
});

// File upload validation
export const validateFileSize = (file: File, maxSizeMB: number): boolean => {
  const maxBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxBytes;
};

export const validateImageFile = (file: File): { valid: boolean; error?: string } => {
  const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
  if (!validTypes.includes(file.type)) {
    return { valid: false, error: 'Only PNG and JPEG images are allowed' };
  }
  if (!validateFileSize(file, 10)) {
    return { valid: false, error: 'Image must be less than 10MB' };
  }
  return { valid: true };
};

export const validateAudioFile = (file: File, type: 'mp3' | 'wav'): { valid: boolean; error?: string } => {
  const validTypes = {
    mp3: ['audio/mpeg', 'audio/mp3'],
    wav: ['audio/wav', 'audio/wave', 'audio/x-wav']
  };
  
  if (!validTypes[type].includes(file.type)) {
    return { valid: false, error: `Invalid ${type.toUpperCase()} file type` };
  }
  
  const maxSizeMB = type === 'mp3' ? 20 : 100;
  if (!validateFileSize(file, maxSizeMB)) {
    return { valid: false, error: `${type.toUpperCase()} must be less than ${maxSizeMB}MB` };
  }
  
  return { valid: true };
};

export const validateProjectFile = (file: File): { valid: boolean; error?: string } => {
  const validExtensions = ['.flp', '.zip', '.rar', '.7z'];
  const hasValidExtension = validExtensions.some(ext => 
    file.name.toLowerCase().endsWith(ext)
  );
  
  if (!hasValidExtension) {
    return { valid: false, error: 'Only FLP, ZIP, RAR, and 7Z files are allowed' };
  }
  
  if (!validateFileSize(file, 500)) {
    return { valid: false, error: 'Project file must be less than 500MB' };
  }
  
  return { valid: true };
};

// Authentication validation schema
export const authSchema = z.object({
  email: z
    .string()
    .trim()
    .email({ message: "Invalid email address" })
    .max(255, { message: "Email must be less than 255 characters" }),
  password: z
    .string()
    .min(6, { message: "Password must be at least 6 characters" })
    .max(100, { message: "Password must be less than 100 characters" }),
  displayName: z
    .string()
    .trim()
    .min(1, { message: "Display name is required" })
    .max(50, { message: "Display name must be less than 50 characters" })
    .regex(/^[a-zA-Z0-9\s._-]+$/, { message: "Display name contains invalid characters" })
    .optional()
});

export type ContactFormData = z.infer<typeof contactFormSchema>;
export type BeatUploadData = z.infer<typeof beatUploadSchema>;
export type AuthData = z.infer<typeof authSchema>;
